import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { forwardRef } from 'react';
import type { ChipProps, ChipVariant, ChipColor, ChipSize } from './Chip.types';
import type { Theme } from '../../theme';

const getSizeStyles = (size: ChipSize) => {
  switch (size) {
    case 'small':
      return css`
        height: 24px;
        font-size: 12px;
        padding: 0 8px;
        
        .chip-icon, .chip-avatar {
          width: 16px;
          height: 16px;
          margin-left: -4px;
          margin-right: 4px;
        }
        
        .chip-delete {
          width: 16px;
          height: 16px;
          margin-left: 4px;
          margin-right: -4px;
        }
      `;
    case 'large':
      return css`
        height: 36px;
        font-size: 14px;
        padding: 0 14px;
        
        .chip-icon, .chip-avatar {
          width: 22px;
          height: 22px;
          margin-left: -6px;
          margin-right: 8px;
        }
        
        .chip-delete {
          width: 20px;
          height: 20px;
          margin-left: 8px;
          margin-right: -6px;
        }
      `;
    case 'medium':
    default:
      return css`
        height: 32px;
        font-size: 13px;
        padding: 0 12px;
        
        .chip-icon, .chip-avatar {
          width: 18px;
          height: 18px;
          margin-left: -4px;
          margin-right: 6px;
        }
        
        .chip-delete {
          width: 18px;
          height: 18px;
          margin-left: 6px;
          margin-right: -4px;
        }
      `;
  }
};

const getFilledColorStyles = (color: ChipColor, theme: Theme) => {
  switch (color) {
    case 'primary':
      return css`
        background-color: ${theme.colors.highlight};
        color: ${theme.colors.highlightText};
        
        &:hover:not([aria-disabled="true"]) {
          filter: brightness(0.9);
        }
        
        .chip-delete:hover {
          background-color: rgba(255, 255, 255, 0.2);
        }
      `;
    case 'success':
      return css`
        background-color: ${theme.colors.semantic.green.base};
        color: ${theme.colors.white};
        
        &:hover:not([aria-disabled="true"]) {
          background-color: ${theme.colors.semantic.green.dark};
        }
        
        .chip-delete:hover {
          background-color: rgba(255, 255, 255, 0.2);
        }
      `;
    case 'error':
      return css`
        background-color: ${theme.colors.semantic.red.base};
        color: ${theme.colors.white};
        
        &:hover:not([aria-disabled="true"]) {
          background-color: ${theme.colors.semantic.red.dark};
        }
        
        .chip-delete:hover {
          background-color: rgba(255, 255, 255, 0.2);
        }
      `;
    case 'warning':
      return css`
        background-color: ${theme.colors.semantic.orange.base};
        color: ${theme.colors.white};
        
        &:hover:not([aria-disabled="true"]) {
          background-color: ${theme.colors.semantic.orange.dark};
        }
        
        .chip-delete:hover {
          background-color: rgba(255, 255, 255, 0.2);
        }
      `;
    case 'info':
      return css`
        background-color: ${theme.colors.semantic.blue.lighter};
        color: ${theme.colors.semantic.blue.dark};
        
        &:hover:not([aria-disabled="true"]) {
          background-color: ${theme.colors.semantic.blue.light};
        }
        
        .chip-delete:hover {
          background-color: rgba(0, 0, 0, 0.1);
        }
      `;
    case 'default':
    default:
      return css`
        background-color: ${theme.colors.grey[200]};
        color: ${theme.colors.grey[800]};
        
        &:hover:not([aria-disabled="true"]) {
          background-color: ${theme.colors.grey[300]};
        }
        
        .chip-delete:hover {
          background-color: rgba(0, 0, 0, 0.1);
        }
      `;
  }
};

const getOutlinedColorStyles = (color: ChipColor, theme: Theme) => {
  switch (color) {
    case 'primary':
      return css`
        border-color: ${theme.colors.highlight};
        color: ${theme.colors.highlight};
        
        &:hover:not([aria-disabled="true"]) {
          background-color: ${theme.colors.highlight}10;
        }
        
        .chip-delete:hover {
          background-color: ${theme.colors.highlight}20;
        }
      `;
    case 'success':
      return css`
        border-color: ${theme.colors.semantic.green.base};
        color: ${theme.colors.semantic.green.dark};
        
        &:hover:not([aria-disabled="true"]) {
          background-color: ${theme.colors.semantic.green.lighter};
        }
        
        .chip-delete:hover {
          background-color: ${theme.colors.semantic.green.lighter};
        }
      `;
    case 'error':
      return css`
        border-color: ${theme.colors.semantic.red.base};
        color: ${theme.colors.semantic.red.dark};
        
        &:hover:not([aria-disabled="true"]) {
          background-color: ${theme.colors.semantic.red.lighter};
        }
        
        .chip-delete:hover {
          background-color: ${theme.colors.semantic.red.lighter};
        }
      `;
    case 'warning':
      return css`
        border-color: ${theme.colors.semantic.orange.base};
        color: ${theme.colors.semantic.orange.dark};
        
        &:hover:not([aria-disabled="true"]) {
          background-color: ${theme.colors.semantic.orange.lighter};
        }
        
        .chip-delete:hover {
          background-color: ${theme.colors.semantic.orange.lighter};
        }
      `;
    case 'info':
      return css`
        border-color: ${theme.colors.semantic.blue.light};
        color: ${theme.colors.semantic.blue.dark};
        
        &:hover:not([aria-disabled="true"]) {
          background-color: ${theme.colors.semantic.blue.lighter};
        }
        
        .chip-delete:hover {
          background-color: ${theme.colors.semantic.blue.lighter};
        }
      `;
    case 'default':
    default:
      return css`
        border-color: ${theme.colors.grey[300]};
        color: ${theme.colors.grey[700]};
        
        &:hover:not([aria-disabled="true"]) {
          background-color: ${theme.colors.grey[100]};
        }
        
        .chip-delete:hover {
          background-color: ${theme.colors.grey[200]};
        }
      `;
  }
};

interface ChipContainerProps {
  variant: ChipVariant;
  color: ChipColor;
  size: ChipSize;
  clickable: boolean;
  hasDelete: boolean;
  isDisabled: boolean;
  $fontSize?: string;
}

const ChipContainer = styled.div<ChipContainerProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  font-family: inherit;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  transition: background-color 0.2s, border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;
  max-width: 100%;
  
  ${props => getSizeStyles(props.size)}
  
  ${props => props.variant === 'outlined'
    ? css`
        background-color: transparent;
        border: 1px solid;
        ${getOutlinedColorStyles(props.color, props.theme as Theme)}
      `
    : css`
        border: none;
        ${getFilledColorStyles(props.color, props.theme as Theme)}
      `
  }
  
  ${props => props.clickable && css`
    cursor: pointer;
    
    &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
    }
  `}
  
  ${props => props.isDisabled && css`
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  `}
  
  ${props => props.$fontSize && css`
    font-size: ${props.$fontSize};
  `}
`;

const IconWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const AvatarWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 50%;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const Label = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  border-radius: 50%;
  color: inherit;
  opacity: 0.7;
  transition: opacity 0.2s, background-color 0.2s;
  flex-shrink: 0;
  
  &:hover {
    opacity: 1;
  }
  
  &:focus-visible {
    outline: none;
    opacity: 1;
  }
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const CloseIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 4L4 12M4 4L12 12"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const Chip = forwardRef<HTMLDivElement, ChipProps>(
  (
    {
      label,
      onDelete,
      variant = 'filled',
      color = 'default',
      size = 'medium',
      fontSize,
      icon,
      avatar,
      disabled = false,
      clickable = false,
      onClick,
      className,
      'data-testid': testId,
      dataTestId,
      dataId,
    },
    ref
  ) => {
    const isClickable = clickable || !!onClick;
    const hasDelete = !!onDelete;

    const handleClick = () => {
      if (!disabled && onClick) {
        onClick();
      }
    };

    const handleDelete = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!disabled && onDelete) {
        onDelete();
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleClick();
      }
    };

    return (
      <ChipContainer
        ref={ref}
        variant={variant}
        color={color}
        size={size}
        $fontSize={fontSize}
        clickable={isClickable}
        hasDelete={hasDelete}
        isDisabled={disabled}
        className={className}
        onClick={handleClick}
        onKeyDown={isClickable ? handleKeyDown : undefined}
        tabIndex={isClickable && !disabled ? 0 : undefined}
        role={isClickable ? 'button' : undefined}
        aria-disabled={disabled || undefined}
        data-testid={dataTestId || testId}
        data-id={dataId}
      >
        {avatar && (
          <AvatarWrapper className="chip-avatar" aria-hidden="true">
            {avatar}
          </AvatarWrapper>
        )}
        {!avatar && icon && (
          <IconWrapper className="chip-icon" aria-hidden="true">
            {icon}
          </IconWrapper>
        )}
        <Label>{label}</Label>
        {onDelete && (
          <DeleteButton
            className="chip-delete"
            onClick={handleDelete}
            type="button"
            aria-label="Remove"
            disabled={disabled}
          >
            <CloseIcon />
          </DeleteButton>
        )}
      </ChipContainer>
    );
  }
);

Chip.displayName = 'Chip';
